import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, SpaceBetween } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import ErrorUtils from 'src/utils/ErrorUtils';
import TemporaryMessage from 'src/models/common/TemporaryMessage';
import AllocationGroup from 'src/models/tp-allocation/AllocationGroup';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { allocationGroupsReducer, ACTIONS } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsReducer';
import { initialState } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsState';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { ACTION_TYPE, TPEBasicModal } from 'src/components/shared/TPEBasicModal';
import AllocationGroupEditorModal from 'src/components/tp-allocation/shared/AllocationGroupEditorModal';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS as DAS_ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import { DasAllocationGroupsContext } from 'src/services/derived-allocation-sheet/DasAllocationGroupsContext';
import { toAllocationGroup } from 'src/services/derived-allocation-sheet/dasAllocationGroupMapper';
import { hasUnsavedFormulas } from 'src/services/derived-allocation-sheet/dasAllocationGroupShares';
import DasAllocationGroup from './DasAllocationGroup';
import DasExpressionLegend from './DasExpressionLegend';
import useDasAllocationFormulaWrites from './useDasAllocationFormulaWrites';
import DasStepInfo from './DasStepInfo';

/** Passed to the closed editor modal, so its reset effect does not run on every render. */
const NO_GROUP = {} as AllocationGroup;

export const SAVE_RETURNED_NO_GROUP_MESSAGE = 'The allocation group was saved, but the response did not include it. Refresh the page to see it.';

/**
 * Step 3 (Configure allocation groups) of the derived allocation sheet builder, adapted from the TPA allocation
 * groups container; keep the two in sync. It reuses the TPA groups reducer and group editor modal. Groups and
 * their formulas load and save through the derived allocation sheet service, and every write refreshes the
 * sheet header. Formulas may reference the saved Step 1 rows and the saved Step 2 totals, both read from the
 * builder state. Formulas save when an edit is done; Save also sends any formula still new or unsaved, and while
 * one is, the step reports unsaved work so the version history waits for it.
 */
export default function DasAllocationGroupsSection(props: { onNextStep?: () => void }) {
    const { onNextStep } = props;
    const { dasState, dasDispatch, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    const { viewMode } = dasState;
    // The builder renders the steps only once a sheet is loaded.
    const sheetVersion = dasState.sheet!.sheetVersion;
    const editable = viewMode === CONSTANTS.VIEW_MODE.EDITABLE;
    const [groupsState, groupsDispatch] = useReducerWithLogger(allocationGroupsReducer, initialState);
    const { groups, groupBeingEdited, removeGroupModalPayload, removeGroupModalVisible, removeFormulaModalPayload } = groupsState;
    const [groupsResult, groupsLoading, loadError] =
        services.derivedAllocationSheetService.getDASAllocationGroups('fetch', sheetId, sheetVersion);
    // Step 2 publishes its totals to the builder state; formulas may reference the saved ones.
    const totals = useMemo(() => dasState.totals.filter((t) => !t.isNew && !!t.description)
        .map((t, i) => ({ description: t.description, visibleSequence: t.visibleSequence ?? `${i + 1}` })), [dasState.totals]);
    const [coaSegments] = services.tpAllocationService.getAllCoaSegments(`das-${sheetId}`);
    const companies = useMemo(() => coaSegments?.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI) ?? [], [coaSegments]);
    const { savingFormula, duplicateFormula, confirmRemoveFormula, saveUnsavedFormulas } = useDasAllocationFormulaWrites({
        services, sheetId, groupsState, groupsDispatch, companies, onWritten: () => dasDispatch(DAS_ACTIONS.REFRESH_SHEET),
    });
    const [isSaving, setIsSaving] = useState(false);
    const [modalMessage, setModalMessage] = useState(undefined as TemporaryMessage | undefined);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (groupsResult == null) {
            return;
        }
        groupsDispatch(ACTIONS.SET_ALLOCATION_GROUPS.withPayload(groupsResult.allocationGroups.map(toAllocationGroup)));
    }, [groupsResult]);

    // A message from an earlier save does not carry over to the next group opened in the editor.
    useEffect(() => setModalMessage(undefined), [groupBeingEdited?.allocationGroupId]);

    // Leaving the editable view closes the group editor and any open delete or remove confirm.
    useEffect(() => {
        if (!editable) {
            groupsDispatch(ACTIONS.SET_GROUP_BEING_EDITED.withPayload(null));
            groupsDispatch(ACTIONS.SET_REMOVE_GROUP_MODAL_PAYLOAD.withPayload(undefined));
            groupsDispatch(ACTIONS.SET_REMOVE_FORMULA_PAYLOAD.withPayload(null));
        }
    }, [viewMode]);

    const openGroupEditor = (group: AllocationGroup | null) => groupsDispatch(ACTIONS.SET_GROUP_BEING_EDITED.withPayload(group));

    const addGroup = () => openGroupEditor({
        allocationGroupId: `newGroup${Date.now()}`, parentEntityId: sheetId, parentEntityVersion: sheetVersion,
        description: '', allocationGroupDescription: '', isNew: true, formulas: [],
    } as unknown as AllocationGroup);

    /** Creates or renames the group from the editor modal; the backend refuses a name another group has. */
    const saveGroup = async (group: AllocationGroup) => {
        setModalMessage(undefined);
        setIsSaving(true);
        const service = services.derivedAllocationSheetService;
        const fields = {
            sheetId, description: group.description.trim(), allocationGroupDescription: group.allocationGroupDescription.trim(),
        };
        try {
            const { allocationGroup } = group.isNew
                ? await service.createDASAllocationGroup(fields)
                : await service.updateDASAllocationGroup({ ...fields, allocationGroupId: group.allocationGroupId });
            if (!mountedRef.current) return;
            if (allocationGroup == null) {
                setModalMessage(new TemporaryMessage(SAVE_RETURNED_NO_GROUP_MESSAGE, 'error', true));
                return;
            }
            groupsDispatch(ACTIONS.ADD_OR_UPDATE_ALLOCATION_GROUP.withPayload({
                ...toAllocationGroup(allocationGroup), formulas: group.formulas ?? [],
            }));
            groupsDispatch(ACTIONS.SET_GROUP_BEING_EDITED.withPayload(null));
            dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
        } catch (ex) {
            if (mountedRef.current) setModalMessage(new TemporaryMessage(ErrorUtils.getMessage(ex), 'error', true));
        } finally {
            if (mountedRef.current) setIsSaving(false);
        }
    };

    /** Deletes the group with its formulas; the backend refuses while the Step 4 mapping points at the group. */
    const confirmDeleteGroup = async () => {
        const group = removeGroupModalPayload;
        if (group == null) return;
        groupsDispatch(ACTIONS.SET_GROUP_LEVEL_ERROR.withPayload({ groupId: group.allocationGroupId, message: undefined }));
        setIsSaving(true);
        try {
            await services.derivedAllocationSheetService.deleteDASAllocationGroup(sheetId, group.allocationGroupId);
            if (!mountedRef.current) return;
            groupsDispatch(ACTIONS.SET_ALLOCATION_GROUPS.withPayload(groups.filter((g) => g.allocationGroupId !== group.allocationGroupId)));
            dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
        } catch (ex) {
            if (mountedRef.current) {
                groupsDispatch(ACTIONS.SET_GROUP_LEVEL_ERROR.withPayload({ groupId: group.allocationGroupId, message: ErrorUtils.getMessage(ex) }));
            }
        } finally {
            if (mountedRef.current) {
                setIsSaving(false);
                groupsDispatch(ACTIONS.SET_REMOVE_GROUP_MODAL_PAYLOAD.withPayload(undefined));
            }
        }
    };

    const saving = isSaving || savingFormula;
    const hasUnsavedWork = editable && (saving || groups.some((g) => hasUnsavedFormulas(g.formulas ?? [])));

    // The builder keeps another version from opening while Step 3 has formulas that are not saved.
    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 3, unsaved: hasUnsavedWork, saving }));
    }, [hasUnsavedWork, saving]);
    useEffect(() => () => dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 3, unsaved: false })), []);

    /** Closes the open editor and saves every formula that is new or edited, as Step 2's Save does for totals. */
    const save = () => {
        groupsDispatch(ACTIONS.EXIT_EDIT_MODE);
        groupsDispatch(ACTIONS.CLEAR_ALL_GROUP_LEVEL_ERRORS);
        saveUnsavedFormulas();
    };
    const contextValue = useMemo(() => ({
        services, groupsState, groupsDispatch, viewMode, sheetId, sheetVersion, saving,
        dataSources: dasState.dataSources, totals, companies, onDuplicateFormula: duplicateFormula,
    }), [groupsState, groupsDispatch, viewMode, sheetId, sheetVersion, saving, dasState.dataSources, totals, companies]);

    return (
        <DasAllocationGroupsContext.Provider value={contextValue}>
            <div className="wizardBoxContentContainer" data-class="dasStep3">
                <SpaceBetween direction="vertical" size="s">
                    <DasStepInfo>
                        Each allocation group computes a per-entity ratio; its formulas allocate up to 100% of the balance. The
                        group name is what the Step 4 mapping column holds.
                    </DasStepInfo>
                    {!!loadError && <Alert type="error" header="Unable to load allocation groups">{loadError}</Alert>}
                    <TPELoadingSpinner loading={groupsLoading} loadingText="Loading allocation groups">
                        <SpaceBetween direction="vertical" size="m">
                            {groups.map((g) => <DasAllocationGroup key={g.allocationGroupId} group={g} />)}
                            {groups.length === 0 && !loadError && (
                                <Box textAlign="center" padding="m">
                                    {editable ? 'There are no allocation groups yet. Click Add allocation group to start.' : 'No allocation groups'}
                                </Box>
                            )}
                        </SpaceBetween>
                    </TPELoadingSpinner>
                    <DasExpressionLegend />
                </SpaceBetween>
                {editable && (
                    <div className="actionButtonsContainer">
                        <Box float="left">
                            <Button data-class="subPrimaryButton" onClick={addGroup} disabled={groupsLoading || saving}>Add allocation group</Button>
                        </Box>
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="m">
                                <Button data-class="saveButton" onClick={save} loading={saving} disabled={groupsLoading || !!loadError}>
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button data-class="nextButton" variant="primary" disabled={groups.length === 0 || groupsLoading || saving}
                                        onClick={() => onNextStep?.()}>Next Step</Button>
                            </SpaceBetween>
                        </Box>
                    </div>
                )}
                {editable && (
                    <AllocationGroupEditorModal
                        group={groupBeingEdited || NO_GROUP}
                        visible={groupBeingEdited != null}
                        modalMessage={modalMessage}
                        onClearModalMessage={() => setModalMessage(undefined)}
                        onModalDismissed={() => openGroupEditor(null)}
                        onModalSubmitted={saveGroup}
                        primaryButtonDisabledLabel={isSaving ? 'Saving...' : undefined}
                    />
                )}
                <TPEBasicModal
                    className="dasGroupDeleteConfirmModal"
                    visible={editable && removeGroupModalVisible}
                    action={ACTION_TYPE.YES_NO}
                    title="Delete allocation group"
                    onCancel={() => groupsDispatch(ACTIONS.SET_REMOVE_GROUP_MODAL_PAYLOAD.withPayload(undefined))}
                    onConfirm={confirmDeleteGroup}
                    primaryButtonDisabledLabel={isSaving ? 'Deleting...' : undefined}
                >
                    <span>Are you sure you want to delete allocation group {removeGroupModalPayload?.description} and its formulas?</span>
                </TPEBasicModal>
                <TPEBasicModal
                    className="dasFormulaDeleteConfirmModal"
                    visible={editable && removeFormulaModalPayload != null}
                    action={ACTION_TYPE.YES_NO}
                    title="Remove formula"
                    onCancel={() => groupsDispatch(ACTIONS.SET_REMOVE_FORMULA_PAYLOAD.withPayload(null))}
                    onConfirm={confirmRemoveFormula}
                    primaryButtonDisabledLabel={savingFormula ? 'Deleting...' : undefined}
                >
                    <span>Are you sure you want to remove formula #{removeFormulaModalPayload?.formula?.visibleSequence} {removeFormulaModalPayload?.formula?.description}?</span>
                </TPEBasicModal>
            </div>
        </DasAllocationGroupsContext.Provider>
    );
}
